import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { showToast } from '@/lib/custom-toast';
import VoiceRecorderWhisper from './VoiceRecorderWhisper';
import { Plus, X, Mic, ArrowLeftRight, CreditCard, Lock, Package, Trash2, ChevronDown, ChevronUp, AlertCircle, Calendar, Sparkles, Zap, User, UserCheck, Building, Store, DollarSign } from 'lucide-react';
import { getTodayLocalDateString } from '@/utils/dateUtils';
import { formatCurrency } from '@/utils/currencyFormat';

const QUICK_MSI_OPTIONS = [3, 6, 9, 12, 18, 24];

const getErrorMessage = (error, defaultMsg) => {
  const detail = error?.response?.data?.detail || error?.message;
  if (!detail) return defaultMsg;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return defaultMsg;
};

const TransactionModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  preselectedType = 'expense',
  editingTransaction = null,
  isDuplicate = false,
  preselectedAccountId = null,
  initialMsi = false,
  initialRetention = false
}) => {
  const { token, user } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [entryMode, setEntryMode] = useState('manual'); // 'manual' | 'voice'
  const [loading, setLoading] = useState(false);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', color: '#3b82f6' });
  const [transferData, setTransferData] = useState({ source_account_id: '', destination_account_id: '', amount: '', description: '' });
  const [newAccount, setNewAccount] = useState({ 
    name: '', 
    type: 'bank', 
    currency: 'MXN', 
    initial_balance: '',
    color: '#3b82f6',
    icon: 'bank'
  });
  const [creditStatus, setCreditStatus] = useState(null);
  const [transferDestCreditStatus, setTransferDestCreditStatus] = useState(null);

  // Estados para Tarjeta de Crédito (Punto 11: Compra Personal vs Pagué por alguien más)
  const [merchant, setMerchant] = useState('');
  const [ownership, setOwnership] = useState('personal'); // 'personal' | 'shared'
  const [personalMode, setPersonalMode] = useState(initialRetention ? 'retention' : (initialMsi ? 'msi' : 'normal'));
  const [personalMsiMonths, setPersonalMsiMonths] = useState(12);
  const [isCustomPersonalMsi, setIsCustomPersonalMsi] = useState(false);
  const [customPersonalMsiMonths, setCustomPersonalMsiMonths] = useState('');
  const [personalMsiMonthsPaid, setPersonalMsiMonthsPaid] = useState(0);
  const [personalRetentionDate, setPersonalRetentionDate] = useState('');
  const [personalRetentionAutoReminder, setPersonalRetentionAutoReminder] = useState(true);

  // Deudor (Pagué por alguien más)
  const [sharedName, setSharedName] = useState('');
  const [sharedEmail, setSharedEmail] = useState('');
  const [sharedPaymentMode, setSharedPaymentMode] = useState('full'); // 'full' | 'installments' | 'retention'
  const [sharedInstallments, setSharedInstallments] = useState(12);
  const [isCustomSharedInstallments, setIsCustomSharedInstallments] = useState(false);
  const [customSharedInstallments, setCustomSharedInstallments] = useState('');
  const [sharedRetentionDate, setSharedRetentionDate] = useState('');
  const [sharedRetentionAutoReminder, setSharedRetentionAutoReminder] = useState(true);
  const [sharedDueDate, setSharedDueDate] = useState('');
  const [sharedAutoReminder, setSharedAutoReminder] = useState(true);

  // SPEI para recibir transferencia del deudor
  const [bankHolder, setBankHolder] = useState(() => {
    try { return localStorage.getItem('saved_bank_holder') || user?.name || ''; } catch { return ''; }
  });
  const [bankName, setBankName] = useState(() => {
    try { return localStorage.getItem('saved_bank_name') || ''; } catch { return ''; }
  });
  const [bankClabe, setBankClabe] = useState(() => {
    try { return localStorage.getItem('saved_bank_clabe') || ''; } catch { return ''; }
  });

  const isDuplicating = Boolean(isDuplicate || (editingTransaction && !editingTransaction.id));
  const isEditing = Boolean(editingTransaction && editingTransaction.id && !isDuplicate);

  // Helper function to get current date in local timezone (YYYY-MM-DD format)
  const getLocalDateString = () => getTodayLocalDateString();

  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: getTodayLocalDateString(),
    category_id: '',
    account_id: '',
    type: preselectedType,
    payment_method: 'Efectivo',
    notes: '',
    is_recurring: false,
    is_investment: false,
    // Phase 2
    is_msi: false,
    msi_months: 12,
    msi_months_paid: 0,
    is_retention: false,
    retention_months: 1,
    items_breakdown: []
  });

  const [showItemsBreakdown, setShowItemsBreakdown] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('');
  const [showQuickInput, setShowQuickInput] = useState(false);
  const [quickText, setQuickText] = useState('');

  const handleProcessQuickText = () => {
    if (!quickText.trim()) return;
    const parts = quickText.split(/[\n,]+/).map(p => p.trim()).filter(Boolean);
    const newItems = [];
    const defaultCat = categories.find(c => c.id === formData.category_id);

    parts.forEach(part => {
      // Regex flexible: captura nombre del producto y monto al final
      const match = part.match(/^(.*?)(?:[:\-\$]|\s+)\$?([0-9]+(?:[\.,][0-9]{1,2})?)$/);
      if (match) {
        const name = match[1].replace(/[:\-\$]/g, '').trim();
        const priceVal = parseFloat(match[2].replace(',', '.'));
        if (name && !isNaN(priceVal) && priceVal > 0) {
          newItems.push({
            id: Date.now().toString() + Math.random().toString(36).substr(2, 4),
            name,
            price: priceVal,
            category_id: defaultCat?.id || '',
            category_name: defaultCat?.name || ''
          });
        }
      }
    });

    if (newItems.length > 0) {
      setFormData(prev => ({
        ...prev,
        items_breakdown: [...(prev.items_breakdown || []), ...newItems]
      }));
      setQuickText('');
      setShowQuickInput(false);
      showToast.success(`✓ ${newItems.length} artículos agregados al desglose`);
    } else {
      showToast.error('Formato no reconocido. Ejemplo: Leche 35, Pan 40');
    }
  };

  // Función helper para limpiar el campo notes de códigos técnicos
  const getDisplayNotes = (notes) => {
    if (!notes) return '';
    // Si contiene código técnico de transferencia, retornar string vacío
    if (notes.startsWith('transfer:')) return '';
    return notes;
  };

  useEffect(() => {
    if (isOpen) {
      if (editingTransaction) {
        setFormData({
          amount: editingTransaction.amount ? editingTransaction.amount.toString() : '',
          description: editingTransaction.description || '',
          date: isDuplicating ? getTodayLocalDateString() : (editingTransaction.date ? editingTransaction.date.split('T')[0] : getTodayLocalDateString()),
          category_id: editingTransaction.category_id || '',
          account_id: editingTransaction.account_id || '',
          type: editingTransaction.type || preselectedType,
          payment_method: editingTransaction.payment_method || 'Efectivo',
          notes: editingTransaction.notes || '',
          is_recurring: editingTransaction.is_recurring || false,
          is_investment: editingTransaction.is_investment || false,
          is_msi: editingTransaction.is_msi || false,
          msi_months: editingTransaction.msi_months || 12,
          msi_months_paid: editingTransaction.msi_months_paid || 0,
          is_retention: editingTransaction.is_retention || false,
          retention_months: editingTransaction.retention_months || 1,
          items_breakdown: editingTransaction.items_breakdown || []
        });
        if (editingTransaction.items_breakdown?.length > 0) {
          setShowItemsBreakdown(true);
        }

        // Prellenado de tarjeta de crédito
        setMerchant(editingTransaction.merchant || '');
        const isSharedTx = Boolean(editingTransaction.shared_with_name || editingTransaction.shared_amount);
        if (isSharedTx) {
          setOwnership('shared');
          setSharedName(editingTransaction.shared_with_name || '');
          setSharedEmail(editingTransaction.shared_email || '');
          const payMode = editingTransaction.is_retention
            ? 'retention'
            : (editingTransaction.is_msi ? 'installments' : 'full');
          setSharedPaymentMode(payMode);
          const inst = editingTransaction.msi_months || editingTransaction.shared_installments || 12;
          setSharedInstallments(inst);
          setSharedRetentionDate(editingTransaction.retention_expected_release_date || '');
          setSharedRetentionAutoReminder(editingTransaction.retention_auto_reminder ?? true);
          setSharedDueDate(editingTransaction.shared_due_date || '');
          setSharedAutoReminder(editingTransaction.shared_auto_reminder ?? true);
          if (editingTransaction.bank_holder) setBankHolder(editingTransaction.bank_holder);
          if (editingTransaction.bank_name) setBankName(editingTransaction.bank_name);
          if (editingTransaction.bank_clabe) setBankClabe(editingTransaction.bank_clabe);
        } else {
          setOwnership('personal');
          const pMode = editingTransaction.is_retention
            ? 'retention'
            : (editingTransaction.is_msi ? 'msi' : 'normal');
          setPersonalMode(pMode);
          const msiM = editingTransaction.msi_months || 12;
          setPersonalMsiMonths(msiM);
          setPersonalMsiMonthsPaid(editingTransaction.msi_months_paid || 0);
          setPersonalRetentionDate(editingTransaction.retention_expected_release_date || '');
          setPersonalRetentionAutoReminder(editingTransaction.retention_auto_reminder ?? true);
        }
      } else {
        // Reset form con tipo preseleccionado
        setFormData({
          amount: '',
          description: '',
          date: getTodayLocalDateString(),
          category_id: '',
          account_id: preselectedAccountId || '',
          type: preselectedType,
          payment_method: 'Efectivo',
          notes: '',
          is_recurring: false,
          is_investment: false,
          is_msi: initialMsi || false,
          msi_months: 12,
          msi_months_paid: 0,
          is_retention: initialRetention || false,
          retention_months: 1,
          items_breakdown: []
        });
        // Preseleccionar cuenta origen en transferencia si venimos desde una cuenta
        setTransferData(prev => ({
          ...prev,
          source_account_id: preselectedAccountId || prev.source_account_id || ''
        }));
        setShowItemsBreakdown(false);

        // Reset estados de tarjeta
        setOwnership('personal');
        setPersonalMode(initialRetention ? 'retention' : (initialMsi ? 'msi' : 'normal'));
        setPersonalMsiMonths(12);
        setIsCustomPersonalMsi(false);
        setCustomPersonalMsiMonths('');
        setPersonalMsiMonthsPaid(0);
        setPersonalRetentionDate('');
        setPersonalRetentionAutoReminder(true);
        setSharedName('');
        setSharedEmail('');
        setSharedPaymentMode('full');
        setSharedInstallments(12);
        setIsCustomSharedInstallments(false);
        setCustomSharedInstallments('');
        setSharedRetentionDate('');
        setSharedRetentionAutoReminder(true);
        setSharedDueDate('');
        setSharedAutoReminder(true);
        setMerchant('');
      }
      
      // Cargar categorías y cuentas después de resetear el formulario
      fetchCategories();
      fetchAccounts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingTransaction, preselectedType, isDuplicate]);

  // useEffect para seleccionar la cuenta (priorizando preselectedAccountId si viene desde una cuenta)
  useEffect(() => {
    if (isOpen && accounts.length > 0 && !editingTransaction) {
      const targetAccount = preselectedAccountId
        ? accounts.find(acc => String(acc.id) === String(preselectedAccountId))
        : (accounts.find(acc => acc.id === formData.account_id) || accounts.find(acc => acc.is_default) || accounts[0]);
      if (targetAccount && (formData.account_id !== targetAccount.id || !formData.payment_method)) {
        setFormData(prev => ({
          ...prev,
          account_id: targetAccount.id,
          payment_method: targetAccount.name
        }));
      }
      if (targetAccount) {
        setTransferData(prev => ({
          ...prev,
          source_account_id: String(preselectedAccountId || targetAccount.id)
        }));
      }
    }
  }, [accounts, isOpen, editingTransaction, preselectedAccountId]);

  // useEffect dedicado para asegurar cuenta origen en transferData al abrir con preselectedAccountId
  useEffect(() => {
    if (isOpen && preselectedAccountId) {
      setTransferData(prev => ({
        ...prev,
        source_account_id: String(preselectedAccountId)
      }));
    }
  }, [isOpen, preselectedAccountId]);

  // useEffect para auto-seleccionar categoría válida para el tipo actual (Punto 15)
  useEffect(() => {
    if (isOpen && categories.length > 0 && !editingTransaction) {
      const availableCats = categories.filter(c => c.type === formData.type);
      const isCurrentValid = availableCats.some(c => c.id === formData.category_id);
      if (!formData.category_id || !isCurrentValid) {
        if (availableCats.length > 0) {
          setFormData(prev => ({
            ...prev,
            category_id: availableCats[0].id
          }));
        }
      }
    }
  }, [categories, isOpen, formData.type, editingTransaction]);

  // Fetch credit card status for formData.account_id
  useEffect(() => {
    if (formData.account_id && token) {
      const acc = accounts.find(a => a.id === formData.account_id);
      if (acc && acc.type === 'credit_card') {
        axios.get(`${API}/accounts/${formData.account_id}/credit-status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => setCreditStatus(res.data))
        .catch(err => console.error('Error fetching credit status:', err));
      } else {
        setCreditStatus(null);
      }
    } else {
      setCreditStatus(null);
    }
  }, [formData.account_id, accounts, token]);

  // Fetch credit card status for transferData.destination_account_id
  useEffect(() => {
    if (transferData.destination_account_id && token) {
      const destAcc = accounts.find(a => a.id === transferData.destination_account_id);
      if (destAcc && destAcc.type === 'credit_card') {
        axios.get(`${API}/accounts/${transferData.destination_account_id}/credit-status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => setTransferDestCreditStatus(res.data))
        .catch(err => console.error('Error fetching dest credit status:', err));
      } else {
        setTransferDestCreditStatus(null);
      }
    } else {
      setTransferDestCreditStatus(null);
    }
  }, [transferData.destination_account_id, accounts, token]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sorted = (response.data || []).sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
      });
      setAccounts(sorted);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  // Función para formatear el monto para visualización
  const formatMoneyInput = (value) => {
    if (!value) return '';
    const strValue = String(value);
    
    // Si termina con punto, preservarlo
    if (strValue.endsWith('.')) {
      const intPart = strValue.slice(0, -1).replace(/[^\d]/g, '');
      if (!intPart) return '.';
      return parseInt(intPart).toLocaleString('es-MX') + '.';
    }
    
    // Si tiene decimales, formatear ambas partes
    const parts = strValue.split('.');
    if (parts.length === 2) {
      const intPart = parts[0].replace(/[^\d]/g, '');
      const decPart = parts[1];
      if (!intPart) return '.' + decPart;
      return parseInt(intPart).toLocaleString('es-MX') + '.' + decPart;
    }
    
    // Solo enteros
    const numbersOnly = strValue.replace(/[^\d]/g, '');
    if (!numbersOnly) return '';
    return parseInt(numbersOnly).toLocaleString('es-MX');
  };

  const handleAmountChange = (e) => {
    const rawValue = e.target.value;
    // Limpiar: solo números y punto
    let numericValue = rawValue.replace(/[^\d.]/g, '');
    
    // Asegurar solo un punto decimal
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      numericValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limitar a 2 decimales
    if (parts[1] && parts[1].length > 2) {
      numericValue = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    setFormData({...formData, amount: numericValue});
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      showToast.error('El nombre de la categoría es requerido');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/categories`,
        {
          name: newCategory.name,
          type: formData.type,
          color: newCategory.color
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast.success('Categoría creada exitosamente');
      setCategories([...categories, response.data]);
      setFormData({ ...formData, category_id: response.data.id });
      setNewCategory({ name: '', color: '#3b82f6' });
      setShowNewCategoryForm(false);
    } catch (error) {
      showToast.error('Error al crear categoría');
    }
  };

  const handleCreateAccount = async () => {
    if (!newAccount.name.trim()) {
      showToast.error('El nombre de la cuenta es requerido');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/accounts`,
        {
          name: newAccount.name,
          type: newAccount.type,
          currency: newAccount.currency,
          initial_balance: parseFloat(newAccount.initial_balance) || 0,
          color: newAccount.color,
          icon: newAccount.icon
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast.success('Cuenta creada exitosamente');
      setAccounts([...accounts, response.data]);
      setFormData({ ...formData, account_id: response.data.id });
      setNewAccount({ name: '', type: 'bank', currency: 'MXN', initial_balance: '', color: '#3b82f6', icon: 'bank' });
      setShowNewAccountForm(false);
    } catch (error) {
      showToast.error('Error al crear cuenta');
    }
  };

  // Cuenta seleccionada y cuentas de banco para SPEI
  const selectedAccount = accounts.find(acc => acc.id === formData.account_id);
  const defaultAccount = accounts.find(acc => acc.is_default);
  const bankAccounts = accounts.filter(a => a.type === 'bank' || a.clabe);

  // Monto numérico parseado
  const parsedAmount = parseFloat(formData.amount) || 0;

  // Cálculos reactivos de MSI Personal
  const activePersonalMsiMonths = isCustomPersonalMsi
    ? Math.max(2, parseInt(customPersonalMsiMonths, 10) || 12)
    : personalMsiMonths;
  const personalMonthlyPayment = activePersonalMsiMonths > 0 ? (parsedAmount / activePersonalMsiMonths) : 0;
  const remainingPersonalMsiMonths = Math.max(0, activePersonalMsiMonths - (parseInt(personalMsiMonthsPaid, 10) || 0));
  const remainingPersonalMsiBalance = Math.max(0, parsedAmount - ((parseInt(personalMsiMonthsPaid, 10) || 0) * personalMonthlyPayment));

  // Cálculos reactivos de Cuotas Compartidas (Deudor)
  const activeSharedInstallments = isCustomSharedInstallments
    ? Math.max(2, parseInt(customSharedInstallments, 10) || 12)
    : sharedInstallments;
  const sharedMonthlyPayment = activeSharedInstallments > 0 ? (parsedAmount / activeSharedInstallments) : 0;

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;

    const totalAmount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(totalAmount) || totalAmount <= 0) {
      showToast.error('Por favor ingresa un monto válido mayor a cero');
      return;
    }

    if (!formData.description || !formData.description.trim()) {
      showToast.error('Por favor ingresa una descripción para el movimiento');
      return;
    }

    if (!formData.account_id) {
      showToast.error('Por favor selecciona o agrega una cuenta antes de continuar');
      return;
    }

    if (!formData.category_id) {
      showToast.error('Por favor selecciona una categoría');
      return;
    }

    setLoading(true);

    try {
      const isCreditCard = selectedAccount?.type === 'credit_card';
      const isShared = isCreditCard && formData.type === 'expense' && ownership === 'shared';

      if (isShared && !sharedName.trim()) {
        showToast.error('Por favor ingresa el nombre de quién te debe');
        setLoading(false);
        return;
      }

      // Guardar datos bancarios SPEI en localStorage
      if (isShared) {
        try {
          if (bankHolder) localStorage.setItem('saved_bank_holder', bankHolder);
          if (bankName) localStorage.setItem('saved_bank_name', bankName);
          if (bankClabe) localStorage.setItem('saved_bank_clabe', bankClabe);
        } catch {}
      }

      // Cálculo de MSI y Retención según tarjeta y titularidad
      let isMsi = false;
      let msiMonths = null;
      let msiMonthsPaid = 0;
      let msiMonthlyPayment = null;
      let isRetention = false;
      let retentionStatus = null;
      let retentionExpectedReleaseDate = null;
      let retentionAutoReminder = false;

      if (isCreditCard && formData.type === 'expense') {
        if (isShared) {
          isMsi = sharedPaymentMode === 'installments';
          msiMonths = isMsi ? activeSharedInstallments : null;
          msiMonthsPaid = 0;
          msiMonthlyPayment = isMsi ? parseFloat(sharedMonthlyPayment.toFixed(2)) : null;

          isRetention = sharedPaymentMode === 'retention';
          retentionStatus = isRetention ? 'active' : null;
          retentionExpectedReleaseDate = isRetention ? (sharedRetentionDate || null) : null;
          retentionAutoReminder = isRetention ? sharedRetentionAutoReminder : false;
        } else {
          isMsi = personalMode === 'msi';
          msiMonths = isMsi ? activePersonalMsiMonths : null;
          msiMonthsPaid = isMsi ? (parseInt(personalMsiMonthsPaid, 10) || 0) : 0;
          msiMonthlyPayment = isMsi ? parseFloat(personalMonthlyPayment.toFixed(2)) : null;

          isRetention = personalMode === 'retention';
          retentionStatus = isRetention ? 'active' : null;
          retentionExpectedReleaseDate = isRetention ? (personalRetentionDate || null) : null;
          retentionAutoReminder = isRetention ? personalRetentionAutoReminder : false;
        }
      }

      const transactionData = {
        amount: totalAmount,
        description: formData.description,
        merchant: merchant.trim() || null,
        date: formData.date,
        category_id: formData.category_id,
        type: formData.type,
        account_id: formData.account_id || null,
        payment_method: formData.payment_method,
        notes: formData.notes,
        is_recurring: formData.is_recurring,
        is_investment: formData.type === 'saving' ? formData.is_investment : false,
        investment_status: (formData.type === 'saving' && formData.is_investment) ? 'active' : null,
        // Tarjeta de Crédito (MSI y Retenciones)
        is_msi: isMsi,
        msi_months: msiMonths,
        msi_months_paid: msiMonthsPaid,
        msi_monthly_payment: msiMonthlyPayment,
        items_breakdown: formData.items_breakdown || [],
        is_retention: isRetention,
        retention_months: isRetention ? 1 : null,
        retention_status: retentionStatus,
        retention_expected_release_date: retentionExpectedReleaseDate,
        retention_auto_reminder: retentionAutoReminder,
        // Pagué por alguien más (Cobro a deudor)
        shared_with_name: isShared ? sharedName.trim() : null,
        shared_email: isShared && sharedEmail ? sharedEmail.trim().toLowerCase() : null,
        shared_amount: isShared ? totalAmount : null,
        shared_due_date: isShared && sharedDueDate ? sharedDueDate : null,
        shared_auto_reminder: isShared && sharedDueDate ? sharedAutoReminder : false,
        bank_holder: isShared ? (bankHolder || user?.name || null) : null,
        bank_name: isShared ? (bankName || null) : null,
        bank_clabe: isShared ? (bankClabe || null) : null,
        shared_payment_mode: isShared ? (sharedPaymentMode === 'installments' ? 'installments' : 'full') : 'full',
        shared_installments: isShared && sharedPaymentMode === 'installments' ? activeSharedInstallments : null,
        shared_installment_amount: isShared && sharedPaymentMode === 'installments' && activeSharedInstallments > 0 ? parseFloat(sharedMonthlyPayment.toFixed(2)) : null
      };

      if (isEditing) {
        await axios.put(
          `${API}/transactions/${editingTransaction.id}`,
          transactionData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showToast.success('Transacción actualizada exitosamente');
      } else {
        await axios.post(
          `${API}/transactions`,
          transactionData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showToast.success(isDuplicating ? 'Transacción duplicada exitosamente' : 'Transacción creada exitosamente');
      }

      window.dispatchEvent(new CustomEvent('transaction-added'));
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving transaction:', error);
      showToast.error(getErrorMessage(error, 'Error al guardar transacción'));
    } finally {
      setLoading(false);
    }
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!transferData.source_account_id || !transferData.destination_account_id) {
      showToast.error('Selecciona cuenta origen y destino');
      return;
    }
    if (transferData.source_account_id === transferData.destination_account_id) {
      showToast.error('Las cuentas deben ser diferentes');
      return;
    }
    if (!transferData.amount || parseFloat(transferData.amount) <= 0) {
      showToast.error('Ingresa un monto válido');
      return;
    }
    setLoading(true);
    try {
      const transferDate = formData.date || getTodayLocalDateString();
      await axios.post(
        `${API}/transfers`,
        {
          from_account_id: transferData.source_account_id,
          to_account_id: transferData.destination_account_id,
          amount: parseFloat(transferData.amount),
          description: transferData.description || 'Transferencia entre cuentas',
          date: transferDate,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success('Transferencia realizada exitosamente');
      setTransferData({ source_account_id: '', destination_account_id: '', amount: '', description: '' });
      window.dispatchEvent(new CustomEvent('transaction-added'));
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error in transfer:', error);
      showToast.error(getErrorMessage(error, 'Error al realizar transferencia'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(cat => cat.type === formData.type);

  const handleModalClose = (open) => {
    if (!open) {
      setEntryMode('manual');
      if (onClose) onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto overscroll-contain p-4 sm:p-6 pb-[max(1.75rem,env(safe-area-inset-bottom))] rounded-3xl">
        <DialogHeader className="pr-6 sm:pr-8">
          <DialogTitle className="text-lg sm:text-xl font-black text-slate-900 leading-snug">
            {isEditing ? 'Editar Transacción' : isDuplicating ? 'Duplicar Transacción' : formData.type === 'transfer' ? 'Transferencia entre Cuentas' : 'Nueva Transacción'}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {isEditing && 'Modifica los datos de la transacción'}
            {isDuplicating && 'Crea una nueva transacción basada en la seleccionada'}
            {!isEditing && !isDuplicating && (
              <>
                {formData.type === 'income' && 'Registra un ingreso'}
                {formData.type === 'expense' && 'Registra un gasto'}
                {formData.type === 'saving' && 'Registra un ahorro'}
                {formData.type === 'transfer' && 'Mueve dinero entre tus cuentas'}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Selector de Modo: Manual vs Voz (IA) */}
        {!isEditing && !isDuplicating && (
          <div className="flex items-center gap-1 p-1 bg-slate-100/90 rounded-2xl mb-1 w-full min-w-0">
            <button
              type="button"
              onClick={() => setEntryMode('manual')}
              className={`flex-1 min-w-0 py-2 px-1.5 rounded-xl text-[11px] sm:text-xs font-bold transition-all truncate ${
                entryMode === 'manual'
                  ? 'bg-white text-slate-900 shadow-sm font-extrabold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              📝 Entrada Manual
            </button>
            <button
              type="button"
              onClick={() => setEntryMode('voice')}
              className={`flex-1 min-w-0 py-2 px-1.5 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate ${
                entryMode === 'voice'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md font-extrabold'
                  : 'text-purple-600 hover:bg-purple-50'
              }`}
            >
              <Mic className="w-3.5 h-3.5 shrink-0 text-purple-600" style={{ color: entryMode === 'voice' ? '#fff' : '#9333ea' }} />
              <span className="truncate">Por Voz (IA)</span>
            </button>
          </div>
        )}

        {entryMode === 'voice' && !editingTransaction ? (
          <div className="py-2">
            <VoiceRecorderWhisper 
              inModal={true}
              onTransactionCreated={() => {
                showToast.success('¡Transacción grabada por voz con éxito!');
                window.dispatchEvent(new CustomEvent('transaction-added'));
                if (onSuccess) onSuccess();
                onClose();
              }}
              onTransferToForm={(voiceData) => {
                setFormData(prev => ({
                  ...prev,
                  amount: voiceData.amount ? String(voiceData.amount) : prev.amount,
                  description: voiceData.description || prev.description,
                  type: voiceData.type || prev.type,
                  category_id: voiceData.category_id || prev.category_id,
                  account_id: voiceData.account_id || prev.account_id,
                  items_breakdown: voiceData.items_breakdown && voiceData.items_breakdown.length > 0 
                    ? voiceData.items_breakdown 
                    : prev.items_breakdown
                }));
                if (voiceData.items_breakdown && voiceData.items_breakdown.length > 0) {
                  setShowItemsBreakdown(true);
                }
                setEntryMode('manual');
                showToast.info('Datos transferidos al formulario.');
              }}
              onCancel={() => {
                setEntryMode('manual');
              }}
            />
          </div>
        ) : formData.type === 'transfer' && !editingTransaction ? (
          /* ── Formulario de Transferencia Inline ── */
          <form onSubmit={handleTransferSubmit} className="space-y-4">
            {/* Tabs (incluidos para poder cambiar de tipo) */}
            <div className="grid grid-cols-4 gap-1 p-1 bg-slate-100/90 rounded-2xl w-full min-w-0">
              <button type="button" onClick={() => setFormData({...formData, type: 'expense', category_id: ''})} className="py-2 sm:py-2.5 rounded-xl font-bold text-xs transition-all truncate text-slate-600 hover:text-slate-900">Gasto</button>
              <button type="button" onClick={() => setFormData({...formData, type: 'income', category_id: ''})} className="py-2 sm:py-2.5 rounded-xl font-bold text-xs transition-all truncate text-slate-600 hover:text-slate-900">Ingreso</button>
              <button type="button" onClick={() => setFormData({...formData, type: 'saving', category_id: ''})} className="py-2 sm:py-2.5 rounded-xl font-bold text-xs transition-all truncate text-slate-600 hover:text-slate-900">Ahorro</button>
              <button type="button" className="py-2 sm:py-2.5 rounded-xl font-bold text-xs transition-all truncate bg-indigo-600 text-white shadow-md shadow-indigo-500/20 font-extrabold">Mover</button>
            </div>

            {/* Cuenta Origen */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Cuenta Origen</Label>
              <Select 
                value={transferData.source_account_id ? String(transferData.source_account_id) : ''} 
                onValueChange={(v) => setTransferData({...transferData, source_account_id: v})}
              >
                <SelectTrigger className="bg-slate-50/70 border-slate-200/80 rounded-2xl h-12">
                  <SelectValue placeholder="Selecciona cuenta origen..." />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={String(acc.id)} disabled={String(acc.id) === String(transferData.destination_account_id)}>
                      {acc.name} — ${(acc.current_balance ?? acc.balance ?? 0).toLocaleString('es-MX')} {acc.currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Flecha */}
            <div className="flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <ArrowLeftRight className="w-5 h-5 text-indigo-600" />
              </div>
            </div>

            {/* Cuenta Destino */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Cuenta Destino</Label>
              <Select 
                value={transferData.destination_account_id ? String(transferData.destination_account_id) : ''} 
                onValueChange={(v) => setTransferData({...transferData, destination_account_id: v})}
              >
                <SelectTrigger className="bg-slate-50/70 border-slate-200/80 rounded-2xl h-12">
                  <SelectValue placeholder="Selecciona cuenta destino..." />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={String(acc.id)} disabled={String(acc.id) === String(transferData.source_account_id)}>
                      {acc.name} — ${(acc.current_balance ?? acc.balance ?? 0).toLocaleString('es-MX')} {acc.currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Si la cuenta destino es Tarjeta de Crédito */}
            {transferDestCreditStatus && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200">
                    <CreditCard className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span>Pago a Tarjeta de Crédito</span>
                  </div>
                  <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                    Deuda: ${(transferDestCreditStatus.current_debt || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {(transferDestCreditStatus.msi_month_payment > 0 || transferDestCreditStatus.non_msi_debt > 0) && (
                  <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500 pt-1 border-t border-slate-200/60">
                    <div>Contado: ${(transferDestCreditStatus.non_msi_debt || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</div>
                    <div>MSI mes: ${(transferDestCreditStatus.msi_month_payment || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</div>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {transferDestCreditStatus.suggested_monthly_payment > 0 && (
                    <button
                      type="button"
                      onClick={() => setTransferData(prev => ({
                        ...prev,
                        amount: transferDestCreditStatus.suggested_monthly_payment.toString(),
                        description: prev.description || 'Pago del mes'
                      }))}
                      className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                    >
                      <span>💡 Pagar Mes:</span>
                      <strong>${transferDestCreditStatus.suggested_monthly_payment.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>
                    </button>
                  )}
                  {transferDestCreditStatus.current_debt > 0 && (
                    <button
                      type="button"
                      onClick={() => setTransferData(prev => ({
                        ...prev,
                        amount: transferDestCreditStatus.current_debt.toString(),
                        description: prev.description || 'Liquidación total'
                      }))}
                      className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                    >
                      <span>💳 Liquidar Total:</span>
                      <strong>${transferDestCreditStatus.current_debt.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Monto */}
            <div className="text-center py-4 bg-slate-50/70 rounded-3xl border border-slate-100">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-1">Cantidad a transferir</span>
              <div className="inline-flex items-center justify-center gap-1">
                <span className="text-3xl font-black text-slate-400">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatMoneyInput(transferData.amount)}
                  onChange={(e) => {
                    let v = e.target.value.replace(/[^\d.]/g, '');
                    const pts = v.split('.');
                    if (pts.length > 2) v = pts[0] + '.' + pts.slice(1).join('');
                    if (pts[1] && pts[1].length > 2) v = pts[0] + '.' + pts[1].substring(0, 2);
                    setTransferData({...transferData, amount: v});
                  }}
                  className="w-48 bg-transparent text-4xl font-black text-slate-900 text-center focus:outline-none placeholder-slate-300"
                  placeholder="0"
                  required
                />
              </div>
            </div>

            {/* Descripción */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Descripción (opcional)</Label>
              <Input
                value={transferData.description}
                onChange={(e) => setTransferData({...transferData, description: e.target.value})}
                placeholder="Ej: Pago tarjeta, ahorro mensual..."
                className="bg-slate-50/70 border-slate-200/80 rounded-2xl h-12"
              />
            </div>

            {/* Botones */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                onPointerDown={(e) => {
                  e.preventDefault();
                  onClose();
                }}
                className="rounded-2xl h-12 px-6 font-bold text-slate-600 border-slate-200 hover:bg-slate-100 text-sm"
              >
                Cancelar
              </Button>
              <button
                type="submit"
                disabled={loading}
                className="h-12 px-8 rounded-2xl font-bold text-white shadow-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-indigo-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
              >
                {loading ? 'Transfiriendo...' : 'Realizar Transferencia'}
              </button>
            </div>
          </form>
        ) : (
          /* ── Formulario Normal de Transacción ── */
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo de Transacción (Tabs) */}
          {!isEditing && (
            <div className="grid grid-cols-4 gap-1 p-1 bg-slate-100/90 rounded-2xl w-full min-w-0">
              <button
                type="button"
                onClick={() => setFormData({...formData, type: 'expense', category_id: ''})}
                className={`py-2 sm:py-2.5 rounded-xl font-bold text-xs transition-all truncate ${
                  formData.type === 'expense'
                    ? 'bg-rose-600 text-white shadow-md shadow-rose-500/20 font-extrabold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Gasto
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, type: 'income', category_id: ''})}
                className={`py-2 sm:py-2.5 rounded-xl font-bold text-xs transition-all truncate ${
                  formData.type === 'income'
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20 font-extrabold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Ingreso
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, type: 'saving', category_id: ''})}
                className={`py-2 sm:py-2.5 rounded-xl font-bold text-xs transition-all truncate ${
                  formData.type === 'saving'
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20 font-extrabold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Ahorro
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, type: 'transfer', category_id: ''})}
                className="py-2 sm:py-2.5 rounded-xl font-bold text-xs transition-all truncate text-slate-600 hover:text-slate-900"
              >
                Mover
              </button>
            </div>
          )}

          {/* Monto Gigante Centrado (Typography XXL Revolut Style) */}
          <div className="text-center py-4 bg-slate-50/70 rounded-3xl border border-slate-100">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-1">Monto de la transacción</span>
            <div className="inline-flex items-center justify-center gap-1">
              <span className="text-3xl font-black text-slate-400">$</span>
              <input
                id="amount"
                type="text"
                inputMode="decimal"
                value={formatMoneyInput(formData.amount)}
                onChange={handleAmountChange}
                className="w-48 bg-transparent text-4xl font-black text-slate-900 text-center focus:outline-none placeholder-slate-300"
                placeholder="0"
                required
              />
            </div>
          </div>

          {/* Descripción Contextual */}
          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              {formData.type === 'income' ? 'Descripción del ingreso *' : formData.type === 'saving' ? 'Descripción del ahorro *' : 'Descripción del gasto *'}
            </Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder={
                formData.type === 'income'
                  ? 'Ej: Sueldo quincenal, Venta de producto, Bono'
                  : formData.type === 'saving'
                  ? 'Ej: Fondo de emergencia, Apartado vacaciones'
                  : 'Ej: Compra en supermercado, Gasolina, Cena'
              }
              className="bg-slate-50/70 border-slate-200/80 rounded-2xl h-12 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-[16px] sm:text-sm font-semibold text-slate-800 placeholder-slate-400"
              required
            />
          </div>

          {/* Fecha */}
          <div className="space-y-1.5 w-full max-w-full">
            <Label htmlFor="date" className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-slate-500" />
              Fecha *
            </Label>
            <div className="w-full max-w-full overflow-hidden rounded-2xl">
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="bg-slate-50/70 border-slate-200/80 rounded-2xl h-12 px-4 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-semibold text-slate-800 w-full max-w-full box-border text-left [&::-webkit-date-and-time-value]:text-left"
                required
              />
            </div>
          </div>

          {/* Cuenta */}
          <div className="space-y-1.5">
            <Label htmlFor="account" className="text-xs font-bold text-slate-700 uppercase tracking-wider">Cuenta</Label>
            {accounts.length === 0 && !showNewAccountForm ? (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-2xl flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                  <p className="text-xs font-bold text-amber-900 dark:text-amber-200">
                    No tienes cuentas registradas. Agrega una para continuar.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setShowNewAccountForm(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shrink-0"
                >
                  + Agregar
                </Button>
              </div>
            ) : !showNewAccountForm ? (
              <div className="flex gap-2">
                <Select 
                  value={formData.account_id || ''} 
                  onValueChange={(value) => {
                    const selectedAccount = accounts.find(acc => acc.id === value);
                    setFormData({
                      ...formData, 
                      account_id: value,
                      payment_method: selectedAccount ? selectedAccount.name : 'Efectivo'
                    });
                  }}
                >
                  <SelectTrigger className="flex-1 bg-slate-50/70 border-slate-200/80 rounded-2xl h-12 text-sm font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all">
                    <SelectValue placeholder="Selecciona una cuenta" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    {accounts.map(account => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name} - ${account.current_balance.toLocaleString('es-MX')} {account.currency}
                        {account.is_default && ' ⭐'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="rounded-2xl h-12 w-12 border-slate-200/80 hover:bg-slate-100"
                  onClick={() => setShowNewAccountForm(true)}
                >
                  <Plus className="h-5 w-5 text-slate-700" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2 p-3 border border-slate-200 rounded-2xl bg-slate-50/50">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-700 uppercase">Nueva Cuenta</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewAccountForm(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <Input placeholder="Nombre de la cuenta" value={newAccount.name} onChange={(e) => setNewAccount({...newAccount, name: e.target.value})} className="bg-white rounded-xl" />
                <div className="grid grid-cols-2 gap-2">
                  <Select value={newAccount.type} onValueChange={(value) => setNewAccount({...newAccount, type: value})}>
                    <SelectTrigger className="bg-white rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="bank">🏦 Banco</SelectItem>
                      <SelectItem value="cash">💵 Efectivo</SelectItem>
                      <SelectItem value="credit_card">💳 Tarjeta</SelectItem>
                      <SelectItem value="digital_wallet">📱 Billetera Digital</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={newAccount.currency} onValueChange={(value) => setNewAccount({...newAccount, currency: value})}>
                    <SelectTrigger className="bg-white rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="MXN">MXN</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="text"
                    placeholder="0.00"
                    value={newAccount.initial_balance ? formatMoneyInput(newAccount.initial_balance) : ''}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                      setNewAccount({...newAccount, initial_balance: numericValue});
                    }}
                    className="pl-7 bg-white rounded-xl"
                  />
                </div>
                <div className="flex gap-2">
                  <Input type="color" value={newAccount.color} onChange={(e) => setNewAccount({...newAccount, color: e.target.value})} className="w-20 rounded-xl" />
                  <Button type="button" onClick={handleCreateAccount} className="flex-1 rounded-xl bg-blue-600 text-white font-bold">Crear Cuenta</Button>
                </div>
              </div>
            )}
            {defaultAccount && !formData.account_id && !showNewAccountForm && (
              <p className="text-xs text-slate-500 pl-1">
                Sugerencia: Usa "{defaultAccount.name}" como cuenta predeterminada
              </p>
            )}

            {/* Tarjeta de Crédito Info / Pago sugerido */}
            {selectedAccount?.type === 'credit_card' && (
              <div className={`mt-2 p-3 sm:p-3.5 rounded-2xl border ${
                formData.type === 'income'
                  ? 'bg-emerald-50/70 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800'
                  : 'bg-indigo-50/70 border-indigo-200 dark:bg-indigo-950/30 dark:border-indigo-800'
              } space-y-2`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold">
                    <CreditCard className="w-4 h-4 text-slate-700 dark:text-slate-300" />
                    <span className={formData.type === 'income' ? 'text-emerald-800 dark:text-emerald-300' : 'text-slate-800 dark:text-slate-200'}>
                      {formData.type === 'income' ? 'Abono / Reembolso a Tarjeta' : 'Cargo a Tarjeta de Crédito'}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                    Deuda actual: ${Math.abs(selectedAccount.current_balance || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {formData.type === 'income' && (
                  <>
                    <p className="text-[11px] text-slate-600 dark:text-slate-400">
                      Registrar este ingreso disminuirá la deuda de la tarjeta.
                    </p>
                    {creditStatus && (
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        {creditStatus.suggested_monthly_payment > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                amount: creditStatus.suggested_monthly_payment.toString(),
                                description: prev.description || `Pago del mes ${selectedAccount.name}`
                              }));
                            }}
                            className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1 shadow-sm"
                          >
                            <span>💡 Pago del mes sugerido:</span>
                            <strong>${creditStatus.suggested_monthly_payment.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>
                          </button>
                        )}
                        {(creditStatus.current_debt > 0 || Math.abs(selectedAccount.current_balance) > 0) && (
                          <button
                            type="button"
                            onClick={() => {
                              const total = creditStatus.current_debt || Math.abs(selectedAccount.current_balance);
                              setFormData(prev => ({
                                ...prev,
                                amount: total.toString(),
                                description: prev.description || `Liquidación total ${selectedAccount.name}`
                              }));
                            }}
                            className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1 shadow-sm"
                          >
                            <span>💳 Liquidar total:</span>
                            <strong>${(creditStatus.current_debt || Math.abs(selectedAccount.current_balance)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Categoría */}
          <div className="space-y-1.5">
            <Label htmlFor="category" className="text-xs font-bold text-slate-700 uppercase tracking-wider">Categoría *</Label>
            {!showNewCategoryForm ? (
              <div className="flex gap-2">
                <Select value={formData.category_id} onValueChange={(value) => setFormData({...formData, category_id: value})}>
                  <SelectTrigger className="flex-1 bg-slate-50/70 border-slate-200/80 rounded-2xl h-12 text-sm font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    {filteredCategories.map(category => (
                      <SelectItem key={category.id} value={category.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{backgroundColor: category.color}} />
                          {category.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" className="rounded-2xl h-12 w-12 border-slate-200/80 hover:bg-slate-100" onClick={() => setShowNewCategoryForm(true)}>
                  <Plus className="h-5 w-5 text-slate-700" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2 p-3 border border-slate-200 rounded-2xl bg-slate-50/50">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-700 uppercase">Nueva Categoría</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewCategoryForm(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <Input placeholder="Nombre de la categoría" value={newCategory.name} onChange={(e) => setNewCategory({...newCategory, name: e.target.value})} className="bg-white rounded-xl" />
                <div className="flex gap-2">
                  <Input type="color" value={newCategory.color} onChange={(e) => setNewCategory({...newCategory, color: e.target.value})} className="w-20 rounded-xl" />
                  <Button type="button" onClick={handleCreateCategory} className="flex-1 rounded-xl bg-blue-600 text-white font-bold">Crear Categoría</Button>
                </div>
              </div>
            )}
          </div>

          {/* Opciones Avanzadas Tarjeta de Crédito: MSI y Retenciones */}
          {(() => {
            const selectedAcc = accounts.find(a => a.id === formData.account_id);
            const isCreditCard = selectedAcc?.type === 'credit_card';

            if (formData.type !== 'expense') return null;

            return (
              <div className="space-y-3">
                {/* Desglose de Artículos de la Compra */}
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                  <div 
                    onClick={() => setShowItemsBreakdown(!showItemsBreakdown)}
                    className="flex items-center justify-between cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-indigo-600" />
                      <span className="text-xs font-bold text-slate-800">
                        Desglose de Artículos {formData.items_breakdown?.length > 0 && `(${formData.items_breakdown.length})`}
                      </span>
                    </div>
                    <button type="button" className="text-slate-400 hover:text-slate-600">
                      {showItemsBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>

                  {showItemsBreakdown && (() => {
                    const totalTx = parseFloat(formData.amount) || 0;
                    const itemsSum = (formData.items_breakdown || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
                    const remaining = Math.max(0, totalTx - itemsSum);
                    const isExceeded = itemsSum > totalTx && totalTx > 0;
                    const isComplete = totalTx > 0 && Math.abs(itemsSum - totalTx) < 0.01;

                    return (
                      <div className="space-y-2.5 pt-1">
                        {/* Barra de balance inteligente */}
                        {totalTx > 0 && (
                          <div className={`p-2.5 rounded-xl text-[11px] font-bold flex items-center justify-between transition-colors ${
                            isComplete 
                              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                              : isExceeded
                              ? 'bg-rose-50 text-rose-800 border border-rose-200'
                              : 'bg-indigo-50/70 text-indigo-900 border border-indigo-100'
                          }`}>
                            <span>
                              {isComplete ? '✓ Todo el monto está desglosado' : isExceeded ? `⚠️ Excede por $${(itemsSum - totalTx).toFixed(2)}` : `Por desglosar: $${remaining.toFixed(2)}`}
                            </span>
                            <span className="font-mono font-black text-xs">
                              ${itemsSum.toFixed(2)} / ${totalTx.toFixed(2)}
                            </span>
                          </div>
                        )}

                        {/* Selector de modo: Entrada rápida vs Uno a uno */}
                        <div className="flex items-center justify-between pb-1">
                          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                            Agregar al ticket
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowQuickInput(!showQuickInput)}
                            className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-lg transition-all"
                          >
                            <Zap className="w-3 h-3 text-indigo-600" />
                            <span>{showQuickInput ? 'Cambiar a 1 por 1' : '⚡ Entrada rápida (Pegar lista)'}</span>
                          </button>
                        </div>

                        {/* Modo Entrada Rápida */}
                        {showQuickInput ? (
                          <div className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-2xl space-y-2">
                            <p className="text-[10px] text-slate-600 font-medium leading-relaxed">
                              Escribe o pega tus artículos separados por coma o renglón:
                              <br />
                              <span className="font-mono font-bold text-indigo-700">Ej: Leche 35, Pan 40, Manzanas 65.50</span>
                            </p>
                            <textarea
                              value={quickText}
                              onChange={(e) => setQuickText(e.target.value)}
                              rows={2}
                              placeholder="Leche 35, Pan 40, Huevos 60..."
                              className="w-full p-2.5 bg-white border border-indigo-200 rounded-xl text-xs text-slate-800 font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowQuickInput(false);
                                  setQuickText('');
                                }}
                                className="px-3 py-1.5 text-xs text-slate-500 font-bold hover:bg-slate-100 rounded-xl transition-all"
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                onClick={handleProcessQuickText}
                                className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm active:scale-95 transition-all flex items-center gap-1"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Agregar artículos</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* Modo 1 por 1 */
                          <div className="space-y-1.5">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Nombre del artículo *"
                              value={newItemName}
                              onChange={(e) => setNewItemName(e.target.value)}
                              className="flex-1 bg-white rounded-xl text-xs h-9"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="$0.00 *"
                              value={newItemPrice}
                              onChange={(e) => setNewItemPrice(e.target.value)}
                              className="w-24 bg-white rounded-xl text-xs h-9"
                            />
                            <Button
                              type="button"
                              onClick={() => {
                                if (!newItemName.trim() || !newItemPrice || parseFloat(newItemPrice) <= 0) return;
                                const catObj = categories.find(c => c.id === (newItemCategory || formData.category_id));
                                const updated = [
                                  ...(formData.items_breakdown || []),
                                  {
                                    id: Date.now().toString(),
                                    name: newItemName.trim(),
                                    price: parseFloat(newItemPrice),
                                    category_id: catObj?.id || '',
                                    category_name: catObj?.name || ''
                                  }
                                ];
                                setFormData({ ...formData, items_breakdown: updated });
                                setNewItemName('');
                                setNewItemPrice('');
                                setNewItemCategory('');
                              }}
                              className="bg-indigo-600 text-white rounded-xl h-9 px-3 text-xs font-bold hover:bg-indigo-700 active:scale-95"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </Button>
                          </div>

                          {/* Selector compacto de categoría por artículo */}
                          <div className="flex items-center gap-2">
                            <select
                              value={newItemCategory || formData.category_id || ''}
                              onChange={(e) => setNewItemCategory(e.target.value)}
                              className="w-full px-2 py-1 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-700 font-medium"
                            >
                              <option value="">Categoría del artículo (heredar de compra)</option>
                              {filteredCategories.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                            {remaining > 0 && !newItemPrice && (
                              <button
                                type="button"
                                onClick={() => setNewItemPrice(remaining.toFixed(2))}
                                className="text-[10px] text-indigo-600 font-bold hover:underline shrink-0 whitespace-nowrap"
                              >
                                Usar saldo restante (${remaining.toFixed(2)})
                              </button>
                            )}
                          </div>
                        </div>
                        )}

                        {/* Lista de artículos */}
                        {formData.items_breakdown?.length > 0 && (
                          <div className="space-y-1.5 pt-1 border-t border-slate-200/80">
                            {formData.items_breakdown.map((item, idx) => (
                              <div key={item.id || idx} className="flex items-center justify-between text-xs bg-white p-2 rounded-xl border border-slate-100">
                                <div className="flex items-center gap-1.5 truncate mr-2">
                                  <span className="font-medium text-slate-800 truncate">{item.name}</span>
                                  {item.category_name && (
                                    <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-md font-bold shrink-0">
                                      {item.category_name}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="font-bold text-slate-900">${parseFloat(item.price).toFixed(2)}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = formData.items_breakdown.filter((_, i) => i !== idx);
                                      setFormData({ ...formData, items_breakdown: updated });
                                    }}
                                    className="text-slate-400 hover:text-red-600 p-1"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}

                            <div className="flex items-center justify-between pt-1">
                              <span className="text-xs font-bold text-slate-600">
                                Total artículos: ${(itemsSum).toFixed(2)}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, amount: itemsSum.toString() });
                                  showToast.success('Monto total actualizado con la suma de artículos');
                                }}
                                className="text-[11px] font-bold text-indigo-600 hover:underline"
                              >
                                Aplicar al monto total
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Si la cuenta es Tarjeta de Crédito: Flujo completo (Punto 11) */}
                {isCreditCard && (
                  <div className="space-y-3 pt-1">
                    {/* Comercio / Tienda Opcional */}
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                        <Store className="w-3.5 h-3.5 text-indigo-500" />
                        <span>Comercio / Tienda (opcional)</span>
                      </label>
                      <input
                        type="text"
                        placeholder={selectedAcc?.credit_card_type === 'department' ? selectedAcc?.name : 'Ej: Liverpool, Amazon, Walmart, Costco'}
                        value={merchant}
                        onChange={(e) => setMerchant(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-slate-50/80 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-semibold text-slate-900 dark:text-white focus:bg-white focus:ring-2 focus:ring-indigo-500/20"
                      />
                    </div>

                    {/* SELECTOR: COMPRA PERSONAL VS PAGUÉ POR ALGUIEN MÁS */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                        ¿A quién le corresponde este gasto?
                      </label>
                      <div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200/60 dark:border-slate-700 w-full max-w-full">
                        <button
                          type="button"
                          onClick={() => setOwnership('personal')}
                          className={`py-2.5 px-2 sm:px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 min-w-0 ${
                            ownership === 'personal'
                              ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-md border border-indigo-200 dark:border-indigo-800'
                              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                          }`}
                        >
                          <User className="w-4 h-4 shrink-0" />
                          <span className="truncate">Compra Personal (Mía)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setOwnership('shared')}
                          className={`py-2.5 px-2 sm:px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 min-w-0 ${
                            ownership === 'shared'
                              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                          }`}
                        >
                          <UserCheck className="w-4 h-4 shrink-0" />
                          <span className="truncate">Pagué por alguien más</span>
                        </button>
                      </div>
                    </div>

                    {/* CASO A: COMPRA PERSONAL (MÍA) */}
                    {ownership === 'personal' && (
                      <div className="p-3.5 sm:p-4 bg-slate-50/90 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3 w-full max-w-full overflow-x-hidden">
                        <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block">
                          Modalidad de Pago en tu Tarjeta:
                        </label>
                        <div className="grid grid-cols-3 gap-1.5 w-full">
                          <button
                            type="button"
                            onClick={() => setPersonalMode('normal')}
                            className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all truncate min-w-0 ${
                              personalMode === 'normal'
                                ? 'bg-rose-600 text-white shadow-sm'
                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                            }`}
                          >
                            💳 Contado
                          </button>
                          <button
                            type="button"
                            onClick={() => setPersonalMode('msi')}
                            className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate min-w-0 ${
                              personalMode === 'msi'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                            }`}
                          >
                            <CreditCard className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">A Meses (MSI)</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPersonalMode('retention')}
                            className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate min-w-0 ${
                              personalMode === 'retention'
                                ? 'bg-amber-600 text-white shadow-sm'
                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                            }`}
                          >
                            <Lock className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">Retención</span>
                          </button>
                        </div>

                        {/* Sub-bloque Personal: MSI */}
                        {personalMode === 'msi' && (
                          <div className="pt-2 space-y-3 border-t border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-blue-900 dark:text-blue-300">
                                Plazo de MSI en Tarjeta:
                              </span>
                              <button
                                type="button"
                                onClick={() => setIsCustomPersonalMsi(!isCustomPersonalMsi)}
                                className="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {isCustomPersonalMsi ? 'Ver opciones comunes' : 'Otro plazo'}
                              </button>
                            </div>

                            {!isCustomPersonalMsi ? (
                              <div className="grid grid-cols-6 gap-1 sm:gap-1.5 w-full">
                                {QUICK_MSI_OPTIONS.map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() => setPersonalMsiMonths(opt)}
                                    className={`py-2 px-0.5 sm:px-1 rounded-xl text-[11px] sm:text-xs font-bold border transition-all min-w-0 text-center ${
                                      personalMsiMonths === opt
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                                    }`}
                                  >
                                    {opt}m
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <input
                                type="number"
                                min="2"
                                max="72"
                                placeholder="Número de meses (ej: 18)"
                                value={customPersonalMsiMonths}
                                onChange={(e) => setCustomPersonalMsiMonths(e.target.value)}
                                className="w-full px-3.5 py-2 bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl text-xs font-bold text-slate-900 dark:text-white"
                              />
                            )}

                            {/* Meses Ya Pagados Previamente */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center pt-1 border-t border-slate-200/60 dark:border-slate-700">
                              <div>
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block">
                                  Meses ya pagados antes
                                </label>
                                <p className="text-[10px] text-slate-500">
                                  Si la compra fue registrada en ciclos pasados.
                                </p>
                              </div>
                              <input
                                type="number"
                                min="0"
                                max={activePersonalMsiMonths - 1}
                                value={personalMsiMonthsPaid}
                                onChange={(e) => setPersonalMsiMonthsPaid(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                className="w-full px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white"
                              />
                            </div>

                            {parsedAmount > 0 && (
                              <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-blue-100 dark:border-blue-900/40 text-xs space-y-1">
                                <div className="flex justify-between font-semibold">
                                  <span className="text-slate-500">Pago mensual en tu tarjeta:</span>
                                  <span className="font-bold text-blue-600">${personalMonthlyPayment.toFixed(2)}/mes</span>
                                </div>
                                <div className="flex justify-between font-semibold text-slate-600 dark:text-slate-300 pt-1 border-t border-slate-100 dark:border-slate-800">
                                  <span>Saldo restante ({remainingPersonalMsiMonths} meses):</span>
                                  <span className="font-black text-slate-900 dark:text-white">${remainingPersonalMsiBalance.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Sub-bloque Personal: Retención */}
                        {personalMode === 'retention' && (
                          <div className="pt-2 space-y-2.5 border-t border-slate-200 dark:border-slate-700">
                            <p className="text-[11px] text-amber-800 dark:text-amber-300">
                              La retención bloquea tu crédito temporalmente. Podrás liberarla o confirmarla como gasto definitivo después.
                            </p>
                            <div className="w-full max-w-full">
                              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-amber-600" />
                                <span>Fecha estimada de liberación</span>
                              </label>
                              <div className="w-full max-w-full overflow-hidden rounded-xl">
                                <input
                                  type="date"
                                  value={personalRetentionDate}
                                  onChange={(e) => setPersonalRetentionDate(e.target.value)}
                                  className="w-full max-w-full box-border min-w-0 h-11 px-3.5 py-2 bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 rounded-xl text-xs sm:text-sm font-bold text-slate-900 dark:text-white text-left [&::-webkit-date-and-time-value]:text-left focus:ring-2 focus:ring-amber-500/20"
                                />
                              </div>
                            </div>
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer pt-1">
                              <input
                                type="checkbox"
                                checked={personalRetentionAutoReminder}
                                onChange={(e) => setPersonalRetentionAutoReminder(e.target.checked)}
                                className="w-4 h-4 text-amber-600 rounded cursor-pointer"
                              />
                              <span>Crear recordatorio de verificación en Recordatorios de Pago</span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CASO B: PAGUÉ POR ALGUIEN MÁS */}
                    {ownership === 'shared' && (
                      <div className="p-3.5 sm:p-4 bg-emerald-50/80 dark:bg-emerald-950/40 rounded-2xl border border-emerald-200 dark:border-emerald-900 space-y-3">
                        {/* Nombre de quien debe */}
                        <div>
                          <label className="text-xs font-bold text-emerald-950 dark:text-emerald-200 block mb-1">
                            ¿Quién te debe el dinero? *
                          </label>
                          <input
                            type="text"
                            required
                            placeholder="Ej: Hermano, Juan, Carlos, Mamá"
                            value={sharedName}
                            onChange={(e) => setSharedName(e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-800 border border-emerald-300 dark:border-emerald-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500/20"
                          />
                        </div>

                        {/* Correo de quien debe */}
                        <div>
                          <label className="text-xs font-bold text-emerald-950 dark:text-emerald-200 block mb-1">
                            Correo de quien te debe (opcional)
                          </label>
                          <input
                            type="email"
                            placeholder="correo@ejemplo.com (para enviar recordatorio si no tiene cuenta)"
                            value={sharedEmail}
                            onChange={(e) => setSharedEmail(e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-800 border border-emerald-300 dark:border-emerald-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500/20"
                          />
                        </div>

                        {/* Plan de cobro para el deudor */}
                        <div className="p-3 bg-white dark:bg-slate-850 rounded-xl border border-emerald-200 dark:border-emerald-800/80 space-y-2.5">
                          <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 block">
                            ¿Cómo te va a pagar {sharedName.trim() || 'esa persona'}?
                          </span>
                          <div className="grid grid-cols-3 gap-1.5 w-full">
                            <button
                              type="button"
                              onClick={() => setSharedPaymentMode('full')}
                              className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all truncate min-w-0 ${
                                sharedPaymentMode === 'full'
                                  ? 'bg-emerald-600 text-white shadow-sm'
                                  : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              💵 Contado
                            </button>
                            <button
                              type="button"
                              onClick={() => setSharedPaymentMode('installments')}
                              className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate min-w-0 ${
                                sharedPaymentMode === 'installments'
                                  ? 'bg-emerald-600 text-white shadow-sm'
                                  : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              📅 <span className="truncate">Cuotas</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setSharedPaymentMode('retention')}
                              className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate min-w-0 ${
                                sharedPaymentMode === 'retention'
                                  ? 'bg-amber-600 text-white shadow-sm'
                                  : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              <Lock className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">Retención</span>
                            </button>
                          </div>

                          {/* Cuotas compartidas */}
                          {sharedPaymentMode === 'installments' && (
                            <div className="pt-2 space-y-2.5 border-t border-slate-100 dark:border-slate-800">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                  Número de cuotas / meses:
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setIsCustomSharedInstallments(!isCustomSharedInstallments)}
                                  className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline"
                                >
                                  {isCustomSharedInstallments ? 'Ver opciones comunes' : 'Otro plazo'}
                                </button>
                              </div>

                              {!isCustomSharedInstallments ? (
                                <div className="grid grid-cols-6 gap-1 sm:gap-1.5 w-full">
                                  {QUICK_MSI_OPTIONS.map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => setSharedInstallments(opt)}
                                      className={`py-2 px-0.5 sm:px-1 rounded-xl text-[11px] sm:text-xs font-bold border transition-all min-w-0 text-center ${
                                        sharedInstallments === opt
                                          ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                          : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                                      }`}
                                    >
                                      {opt}m
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <input
                                  type="number"
                                  min="2"
                                  max="72"
                                  placeholder="Número de cuotas (ej: 5, 8, 15)"
                                  value={customSharedInstallments}
                                  onChange={(e) => setCustomSharedInstallments(e.target.value)}
                                  className="w-full px-3.5 py-2 bg-white dark:bg-slate-800 border border-emerald-300 dark:border-emerald-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white"
                                />
                              )}

                              {parsedAmount > 0 && (
                                <p className="text-[11px] text-emerald-700 dark:text-emerald-300 font-bold bg-emerald-50/70 dark:bg-emerald-950/40 p-2 rounded-lg">
                                  ✓ {sharedName.trim() || 'El deudor'} pagará {activeSharedInstallments} cuotas de {formatCurrency(sharedMonthlyPayment)} al mes
                                </p>
                              )}
                            </div>
                          )}

                          {/* Retención compartida */}
                          {sharedPaymentMode === 'retention' && (
                            <div className="pt-2 space-y-2 border-t border-slate-100 dark:border-slate-800 w-full max-w-full">
                              <div className="w-full max-w-full">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Fecha estimada de liberación de retención</span>
                                </label>
                                <div className="w-full max-w-full overflow-hidden rounded-xl">
                                  <input
                                    type="date"
                                    value={sharedRetentionDate}
                                    onChange={(e) => setSharedRetentionDate(e.target.value)}
                                    className="w-full max-w-full box-border min-w-0 h-11 px-3.5 py-2 bg-slate-50 dark:bg-slate-800 border border-amber-300 dark:border-amber-700 rounded-xl text-xs sm:text-sm font-bold text-slate-900 dark:text-white text-left [&::-webkit-date-and-time-value]:text-left focus:ring-2 focus:ring-amber-500/20"
                                  />
                                </div>
                              </div>
                              <label className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer pt-1">
                                <input
                                  type="checkbox"
                                  checked={sharedRetentionAutoReminder}
                                  onChange={(e) => setSharedRetentionAutoReminder(e.target.checked)}
                                  className="w-4 h-4 text-amber-600 rounded cursor-pointer"
                                />
                                <span>Recordatorio de verificación de retención</span>
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Datos Bancarios SPEI */}
                        <div className="p-3 bg-white dark:bg-slate-850 rounded-xl border border-emerald-200 dark:border-emerald-800/80 space-y-2 w-full max-w-full">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 pb-1 border-b border-emerald-100 dark:border-emerald-900/60">
                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 shrink-0">
                              <Building className="w-3.5 h-3.5 text-emerald-600" />
                              <span>Datos para recibir transferencia (SPEI)</span>
                            </span>
                            {bankAccounts.length > 0 && (
                              <select
                                onChange={(e) => {
                                  const acc = bankAccounts.find(a => a.id === e.target.value);
                                  if (acc) {
                                    setBankName(acc.bank_name || acc.name);
                                    if (acc.clabe) setBankClabe(acc.clabe);
                                    if (acc.holder_name) setBankHolder(acc.holder_name);
                                    else if (!bankHolder) setBankHolder(user?.name || '');
                                  }
                                }}
                                className="text-[11px] text-indigo-600 dark:text-indigo-400 font-bold bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 cursor-pointer outline-none w-full sm:w-auto"
                              >
                                <option value="">⚡ Cargar de mis cuentas...</option>
                                {bankAccounts.map(b => (
                                  <option key={b.id} value={b.id}>
                                    {b.name}{b.clabe ? ' (✓ CLABE)' : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>

                          <div className="space-y-2 pt-0.5">
                            <div>
                              <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Titular</label>
                              <input
                                type="text"
                                placeholder="Nombre completo"
                                value={bankHolder}
                                onChange={(e) => setBankHolder(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-white"
                              />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Banco</label>
                                <input
                                  type="text"
                                  placeholder="Ej: BBVA, Nu, Banorte"
                                  value={bankName}
                                  onChange={(e) => setBankName(e.target.value)}
                                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-white"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">CLABE (18 dígitos)</label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={18}
                                  placeholder="012345678901234567"
                                  value={bankClabe}
                                  onChange={(e) => setBankClabe(e.target.value.replace(/\D/g, ''))}
                                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-mono font-bold tracking-wider text-slate-900 dark:text-white"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Fecha Límite Opcional y Recordatorio */}
                        <div className="space-y-2">
                          <div>
                            <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300 block mb-1">
                              Fecha límite para que te pague (opcional)
                            </label>
                            <input
                              type="date"
                              value={sharedDueDate}
                              onChange={(e) => setSharedDueDate(e.target.value)}
                              className="w-full px-3.5 py-2 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs font-medium text-slate-900 dark:text-white"
                            />
                          </div>
                          {sharedDueDate && (
                            <label className="flex items-start gap-2 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer pt-0.5 select-none">
                              <input
                                type="checkbox"
                                checked={sharedAutoReminder}
                                onChange={(e) => setSharedAutoReminder(e.target.checked)}
                                className="w-4 h-4 mt-0.5 text-emerald-600 rounded cursor-pointer shrink-0"
                              />
                              <span>Crear recordatorio automático previo a la fecha límite</span>
                            </label>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Notas */}
          <div className="space-y-1.5">
            <Label htmlFor="notes" className="text-xs font-bold text-slate-700 uppercase tracking-wider">Notas</Label>
            <Textarea
              id="notes"
              value={getDisplayNotes(formData.notes)}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Notas adicionales (opcional)"
              rows={3}
              className="bg-slate-50/70 border-slate-200/80 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-medium text-slate-800 placeholder-slate-400 p-3.5"
            />
          </div>

          {/* Botones */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              onPointerDown={(e) => {
                e.preventDefault();
                onClose();
              }}
              disabled={showNewAccountForm || showNewCategoryForm}
              className="rounded-2xl h-12 px-6 font-bold text-slate-600 border-slate-200 hover:bg-slate-100 text-sm"
            >
              Cancelar
            </Button>
            <button 
              type="submit" 
              disabled={loading || showNewAccountForm || showNewCategoryForm}
              onPointerDown={(e) => {
                if (!loading && !showNewAccountForm && !showNewCategoryForm) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              onClick={(e) => {
                if (!loading && !showNewAccountForm && !showNewCategoryForm) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              className={`h-12 px-8 rounded-2xl font-bold text-white shadow-lg active:scale-98 transition-all text-sm flex items-center justify-center gap-2 ${
                formData.type === 'income' 
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-emerald-500/25' 
                  : formData.type === 'saving'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/25'
                    : 'bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 shadow-rose-500/25'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? 'Guardando...' : isEditing ? 'Guardar Cambios' : isDuplicating ? 'Duplicar Transacción' : 'Crear Transacción'}
            </button>
          </div>
        </form>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default TransactionModal;
